import React, { useEffect, useRef, useState } from 'react';
import { ButtonBubbleIcon } from '../../../../../patterns/00-atoms/button/button.tsx';
import { Icon } from '../../../../../patterns/00-atoms/icon/icon.tsx';
import { RestaurantDetails_getRestaurantDetails_menuCard_categories_entries } from '../../../../../types/graphql-types-generated.ts';
import { Price } from '../../../../../patterns/00-atoms/price/price.tsx';
import { classList } from '../../../../../utils/string.ts';
import { extractAllergenClassName } from '../../../../../utils/allergens.tsx';
import { isNonEmptyArray, joinJsx } from '../../../../../utils/array.tsx';
import { Toggletip } from '../../../../../patterns/00-atoms/toggletip/toggletip.tsx';
import { insertFoodTags } from '../../../../../utils/foodtags.tsx';
import { useAppContext } from '../../../../../components/app-context/app-context.tsx';
import { likeDish } from './like-dish.graphql.ts';
import { UBTracking } from '../../../../../components/tracking/ubtracking.ts';
import { AVERAGE_NUMBER_OF_RESTAURANTS } from '../../../../../utils/dishes.ts';
import Lightbox from 'yet-another-react-lightbox';
import { createGraphQLService } from '../../../../../../../server/source/services/endpoint-factory.ts';
import { useIsSsr } from '../../../../../utils/hooks/use-is-ssr.tsx';
import { OneClickElements } from '../../../../../utils/oneClickElements.ts';
import { Cookie } from '../../../../../utils/cookie.ts';
import { DividerWrapper } from './dividerWrapper.tsx';
import { matchesCohortFromCookie } from '../../../../../utils/cohort.ts';

export type DishProps = {
  entry: RestaurantDetails_getRestaurantDetails_menuCard_categories_entries;
  dishCount?: number;
  showPrices?: boolean;
};

export const Dish: React.FunctionComponent<DishProps> = props => {
  const { appContext } = useAppContext();
  const { isSsr } = useIsSsr();
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const abTabSetup = matchesCohortFromCookie('sk-AB-tabsetup', '2', '3', '4');

  const showPrices = props.showPrices ?? true;
  const entry = props.entry;

  let imageUrl = entry?.imageUrl;
  let thumbnailUrl = entry?.thumbnailUrl;
  let name = entry?.name;
  let description = entry?.description;
  let prices = entry?.prices;
  let likesCount = entry?.likesCount;
  let isVegan = entry?.isVegan;
  let isVegetarian = entry?.isVegetarian;
  let allergenList = entry?.allergenList;
  let foodTags = entry?.foodTags;

  const allergenListAddition = allergenList?.filter(allergen => allergen.type === 'addition');
  const allergenListAllergen = allergenList?.filter(allergen => allergen.type === 'allergen');

  const dishId = props.entry.id.toString();
  const userId = appContext?.user ? appContext.user.id : null;

  const [galleryOpen, setGalleryOpen] = useState(false);
  const likeButtonRef = useRef<HTMLAnchorElement>(null);
  const bookmarkButtonRef = useRef<HTMLAnchorElement>(null);
  const dishElement = useRef<HTMLDivElement>(null);
  const dishPositions: number[] = [
    7,
    AVERAGE_NUMBER_OF_RESTAURANTS / 2,
    AVERAGE_NUMBER_OF_RESTAURANTS
  ];

  const Divider = <div className="text-sk-grey-900 text-xs">•</div>;

  let Prices: React.JSX.Element[] = [];
  let VeganOrVegetarian: React.JSX.Element | null = null;
  let Allergens: React.JSX.Element | null = null;

  if (prices && showPrices) {
    Prices = [
      <DividerWrapper
        key="prices"
        divider={Divider}
        children={prices
          .filter(price => price !== null)
          .map((price, index) => (
            <Price price={price.price} className="whitespace-nowrap" key={index}>
              {price.title}
            </Price>
          ))}
      />
    ];
  }

  if (isVegan || isVegetarian) {
    VeganOrVegetarian = (
      <>
        <Icon name="vegan-small" className="text-sk-green-500 text-base" />
        <span>{isVegan ? 'vegan' : 'vegetarisch'}</span>
      </>
    );
  }

  if (isNonEmptyArray(allergenListAddition) || isNonEmptyArray(allergenListAllergen)) {
    Allergens = (
      <>
        <Toggletip
          button={buttonProps => (
            <button
              {...buttonProps}
              className={classList('flex items-center gap-0.5', buttonProps.className)}
              aria-label={[...(allergenListAllergen || []), ...(allergenListAddition || [])]
                .map(allergen => allergen.description)
                .join(', ')}
            >
              {allergenListAllergen?.map(allergen => (
                <Icon
                  name={extractAllergenClassName(allergen?.iconClass)}
                  className="text-base"
                  aria-label={allergen.description}
                  key={allergen.key}
                />
              ))}

              {isNonEmptyArray(allergenListAddition) && (
                <div className="flex flex-row flex-wrap items-center">
                  (
                  {joinJsx(
                    allergenListAddition.map(allergen => (
                      <div key={allergen.key} className={`text-sm`}>
                        {allergen.key}
                      </div>
                    )),
                    <span>,&nbsp;</span>
                  )}
                  )
                </div>
              )}
            </button>
          )}
          content={
            <>
              {allergenListAllergen?.map(allergen => (
                <div className="flex items-center p-2" key={allergen.key}>
                  <Icon
                    name={extractAllergenClassName(allergen?.iconClass)}
                    className="mr-2 text-2xl"
                    aria-label={allergen.description}
                  />
                  {allergen.description}
                </div>
              ))}

              {allergenListAddition?.map(allergen => (
                <div className="flex h-6 items-center p-2" key={allergen.key}>
                  <div className="mr-2 flex min-w-6">({allergen.key})</div> {allergen.description}
                </div>
              ))}
            </>
          }
        />
      </>
    );
  }

  const handleLike = () => {
    UBTracking.trackEvent.click('menu_card', 'like', dishId);
    setIsLiked(true);
    // appContext.graphqlService = createGraphQLService(appContext, 'public');
    // likeDish({ dishId, userId: null }, appContext)
    //   .then(response => {
    //     const menuEntryLikeId = response.data?.saveMenuEntryLike;

    //     if (!menuEntryLikeId) {
    //       console.error('Like dish failed', { dishId, response });
    //       return;
    //     }
    //     setIsLiked(true);

    //     const oce = new OneClickElements();
    //     const oceCookieValue = oce
    //       .likeId(Number(dishId))
    //       .menuEntryLikeValue(menuEntryLikeId).elementsString;
    //     const cookie = new Cookie('oneClickElements');
    //     cookie.path = '/';
    //     cookie.expires = new Date(Date.now() + 1000 * 60 * 60 * 24); // 24 Stunden
    //     cookie.value = oceCookieValue;

    //     window.location.href = 'https://www.speisekarte.de/anmelden';
    //   })
    //   .catch(() => {
    //     // ... 🤷🏻‍♂️
    //     console.log('Dish liked failed', {
    //       dishId
    //     });
    //   });
  };

  const handleBookmark = () => {
    UBTracking.trackEvent.click('menu_card', 'bookmark', dishId);
    setIsBookmarked(true);
  };

  const handleOpenLightbox = () => {
    setGalleryOpen(true);
    UBTracking.state.dish = { id: dishId };
    UBTracking.trackEvent.click('menu_card', 'image');
    UBTracking.trackEvent.click('menu_card', 'lightbox', 'open');
  };

  const handleCloseLightbox = () => {
    setGalleryOpen(false);
    UBTracking.trackEvent.click('menu_card', 'lightbox', 'close');
  };

  useEffect(() => {
    if (isSsr) {
      return;
    }

    if (likeButtonRef.current) {
      UBTracking.onTrackTargetView(likeButtonRef.current, 'menu_card', 'like', dishId);
    }

    if (bookmarkButtonRef.current && abTabSetup) {
      UBTracking.onTrackTargetView(bookmarkButtonRef.current, 'menu_card', 'bookmark', dishId);
    }

    if (!dishPositions.includes(props.dishCount || 0)) {
      return;
    }

    let value = '';

    switch (props.dishCount) {
      case 7:
        value = 'seventh';
        break;
      case AVERAGE_NUMBER_OF_RESTAURANTS / 2:
        value = 'half_of_average';
        break;
      case AVERAGE_NUMBER_OF_RESTAURANTS:
        value = 'average';
        break;
    }

    if (value === '') {
      return;
    }

    UBTracking.state.dish = { id: entry.id.toString() };
    UBTracking.trackTarget(dishElement.current, {
      view: {
        category: 'scroll_depth',
        label: 'dish_count',
        value
      }
    });
  });

  return (
    <>
      <div
        className="bg-sk-grey-0 md:border-sk-grey-500 flex justify-between gap-4 py-4 md:rounded-3xl md:border md:px-4"
        ref={dishElement}
      >
        <div className="text-sk-blue-500 flex flex-col gap-1 font-medium">
          <h4 className="text-lg leading-5 font-bold">{insertFoodTags(name, foodTags)}</h4>
          <div className="flex flex-row flex-wrap items-center gap-1 text-sm">
            <DividerWrapper
              divider={Divider}
              children={[
                <DividerWrapper divider={Divider} children={Prices} />,
                <div className="whitespace-nowrap">
                  <Icon name="like-small" className="align-text-top text-base" />
                  <span>{likesCount}</span>
                </div>,
                VeganOrVegetarian,
                Allergens
              ]}
            />
          </div>
          <p className="text-sk-grey-900 text-sm whitespace-pre-line">
            {insertFoodTags(description, foodTags)}
          </p>
        </div>
        <div className={classList('relative w-24', thumbnailUrl && 'h-24 shrink-0')}>
          {thumbnailUrl && (
            <img
              className="h-full w-full cursor-pointer rounded-3xl object-cover"
              src={thumbnailUrl}
              alt={name}
              loading="lazy"
              onClick={handleOpenLightbox}
              onLoad={event => {
                UBTracking.onTrackTargetView(event.target, 'menu_card', 'IMAGE', {
                  dish: { id: dishId }
                });
              }}
            />
          )}
          <div
            className={classList(
              'absolute',
              thumbnailUrl ? 'right-2 bottom-2' : 'top-0 right-0',
              'flex flex-row gap-1'
            )}
          >
            {abTabSetup && (
              <ButtonBubbleIcon
                ref={bookmarkButtonRef}
                iconName={isBookmarked ? 'bookmark-filled' : 'bookmark'}
                onClick={handleBookmark}
              />
            )}
            <ButtonBubbleIcon
              ref={likeButtonRef}
              iconName={isLiked ? 'like-filled' : 'like'}
              onClick={handleLike}
            />
          </div>
        </div>
      </div>
      {imageUrl && (
        <Lightbox
          open={galleryOpen}
          close={handleCloseLightbox}
          slides={[{ src: imageUrl }]}
          carousel={{
            finite: true
          }}
          render={{
            buttonPrev: () => null,
            buttonNext: () => null
          }}
        />
      )}
    </>
  );
};
